import React from 'react';
import { Check, CheckCheck, Clock3, Loader2, Mic, RotateCw, X } from 'lucide-react';
import type { Message } from '../../types';
import { cn, ui } from '../../utils';
import { formatAbsoluteMessageTime, formatMessageTime } from '../../utils/formatMessageTime';

interface MessageMetaProps {
  message: Message;
  isUser: boolean;
  timeReference: number;
  align?: 'left' | 'right';
  onRetry?: (messageId: string) => void;
  onDismiss?: (messageId: string) => void;
  className?: string;
}

function getStatusCopy(message: Message) {
  const errorReason = message.error || (typeof message.metadata?.error === 'string' ? message.metadata.error : '');
  const sentAt = formatAbsoluteMessageTime(message.sentAt || message.createdAt || message.timestamp);
  const deliveredAt = formatAbsoluteMessageTime(message.deliveredAt || message.sentAt || message.createdAt || message.timestamp);

  switch (message.status) {
    case 'queued':
      return { title: 'Queued until you reconnect', label: 'Queued', errorReason: '' };
    case 'sending':
      return { title: 'Sending…', label: 'Sending', errorReason: '' };
    case 'sent':
      return { title: `Sent at ${sentAt}`, label: 'Sent', errorReason: '' };
    case 'delivered':
      return { title: `Delivered at ${deliveredAt}`, label: 'Delivered', errorReason: '' };
    case 'failed':
      return { title: errorReason ? `Failed: ${errorReason}` : 'Failed to send', label: 'Failed', errorReason };
    default:
      return { title: '', label: '', errorReason: '' };
  }
}

function DeliveryIndicator({ message }: { message: Message }) {
  const status = message.status;
  const copy = getStatusCopy(message);

  if (!status) {
    return null;
  }

  if (status === 'queued') {
    return <Clock3 className="h-3.5 w-3.5 text-amber-300" aria-label={copy.label} />;
  }

  if (status === 'sending') {
    return <Loader2 className="h-3.5 w-3.5 animate-spin" aria-label={copy.label} />;
  }

  if (status === 'sent') {
    return <Check className="h-3.5 w-3.5" aria-label={copy.label} />;
  }

  if (status === 'delivered') {
    return <CheckCheck className="h-3.5 w-3.5" aria-label={copy.label} />;
  }

  if (status === 'failed') {
    return <X className="h-3.5 w-3.5 text-red-400" aria-label={copy.label} />;
  }

  return null;
}

export function MessageMeta({
  message,
  isUser,
  timeReference,
  align = isUser ? 'right' : 'left',
  onRetry,
  onDismiss,
  className,
}: MessageMetaProps) {
  const timestampSource = message.status === 'streaming'
    ? new Date(timeReference).toISOString()
    : message.createdAt || message.timestamp || new Date(timeReference).toISOString();
  const timestampLabel = message.status === 'streaming'
    ? 'just now'
    : formatMessageTime(timestampSource, timeReference);
  const absoluteTimestamp = formatAbsoluteMessageTime(timestampSource);
  const statusCopy = getStatusCopy(message);
  const showFailureRow = isUser && message.status === 'failed';
  const showQueuedRow = isUser && message.status === 'queued';
  // Voice Mode transcripts are visually differentiated from typed messages
  // (spec §6.2) with a small mic badge next to the timestamp.
  const isVoiceTranscript = message.metadata?.source === 'voice';
  const voiceBadge = isVoiceTranscript ? (
    <span className="inline-flex items-center gap-1" title="Voice mode transcript" data-testid="voice-transcript-badge">
      <Mic className="h-3 w-3" aria-hidden="true" />
      <span>Voice</span>
    </span>
  ) : null;

  return (
    <div className={cn('mt-1 px-1 text-xs transition-opacity duration-150', align === 'right' ? 'text-right' : 'text-left', className)}>
      <div
        className={cn(
          'flex items-center gap-1.5 text-muted-foreground',
          align === 'right' ? 'justify-end' : 'justify-start',
          isUser && message.status === 'failed' && 'text-red-400',
        )}
      >
        {align === 'right' ? (
          <>
            {voiceBadge}
            <span title={absoluteTimestamp}>{timestampLabel}</span>
            {isUser ? <span title={statusCopy.title}><DeliveryIndicator message={message} /></span> : null}
          </>
        ) : (
          <>
            {voiceBadge}
            {isUser ? <span title={statusCopy.title}><DeliveryIndicator message={message} /></span> : null}
            <span title={absoluteTimestamp}>{timestampLabel}</span>
          </>
        )}
      </div>

      {showQueuedRow ? (
        <div className={cn('mt-1 flex items-center gap-2 text-amber-300', align === 'right' ? 'justify-end' : 'justify-start')}>
          <span>Queued, will send when connected</span>
        </div>
      ) : null}

      {showFailureRow ? (
        <div className={cn('mt-1 flex items-center gap-2 text-red-400', align === 'right' ? 'justify-end' : 'justify-start')}>
          <span>
            Failed to send{statusCopy.errorReason ? ` · ${statusCopy.errorReason}` : ''}
          </span>
          {onRetry ? (
            <button
              type="button"
              onClick={() => onRetry(message.id)}
              className={cn(ui.button.base, 'min-h-[32px] gap-1 rounded-lg px-1.5 py-0.5 text-xs font-medium text-red-300 hover:bg-red-500/10 hover:text-red-200 focus-visible:ring-red-400/50')}
            >
              <RotateCw className="h-3.5 w-3.5" />
              Retry
            </button>
          ) : null}
          {onDismiss ? (
            <button
              type="button"
              onClick={() => onDismiss(message.id)}
              className={cn(ui.button.icon, 'h-8 w-8 rounded-lg border-0 p-0 text-red-300 hover:bg-red-500/10 hover:text-red-200 focus-visible:ring-red-400/50')}
              aria-label="Dismiss failed message"
              title="Dismiss failed message"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default MessageMeta;
